export interface ServiceStrength {
  icon: string;
  title: string;
  text: string;
}

export interface ServiceCapability {
  icon: string;
  title: string;
  text: string;
}

export interface ServiceDetailFaq {
  question: string;
  answer: string;
}

export interface ServiceDetailData {
  slug: string;
  name: string;
  shortName: string;
  icon: string;
  eyebrow: string;
  heroTitle: string;
  heroDescription: string;
  heroImage: string;
  overviewHeading: string;
  overviewParagraphs: string[];
  overviewImage: string;
  strengthsHeading: string;
  strengthsIntro: string;
  strengths: ServiceStrength[];
  capabilitiesHeading: string;
  capabilitiesIntro: string;
  capabilities: ServiceCapability[];
  faqs: ServiceDetailFaq[];
}

export const serviceDetails: ServiceDetailData[] = [
  {
    slug: 'power-generation',
    name: 'Power Generation & Generators',
    shortName: 'Power Generation',
    icon: 'ri-flashlight-line',
    eyebrow: 'Power Generation & Generators',
    heroTitle: 'Power That Never Lets Your Business Down',
    heroDescription:
      'Generator sales, turnkey installation and round-the-clock servicing — sized correctly, installed properly and supported for the long run.',
    heroImage:
      `${import.meta.env.BASE_URL}images/afb6ac41de6c3dcf.webp`,
    overviewHeading: 'Dependable Power, Engineered Around Your Load',
    overviewParagraphs: [
      'For any operation where an outage means lost production, spoiled stock or missed commitments, dependable power is not a luxury — it is a lifeline. John Holt supplies, installs and maintains generators and power systems sized precisely for the load they need to carry.',
      'From a single standby generator for a retail outlet to multi-unit systems for industrial facilities, our engineers manage the full journey — load assessment, equipment selection, supply, installation, commissioning, servicing and genuine spare parts.',
    ],
    overviewImage:
      `${import.meta.env.BASE_URL}images/684eb47678b0e5b7.jpg`,
    strengthsHeading: 'Why Clients Choose Our Power Team',
    strengthsIntro:
      'Every installation is backed by the same discipline: do it right the first time, then keep it running.',
    strengths: [
      {
        icon: 'ri-scales-3-line',
        title: 'Right-sized from the start',
        text: 'We calculate your true load and duty cycle so you never pay for more power than you need — or come up short when it counts.',
      },
      {
        icon: 'ri-hammer-line',
        title: 'Turnkey installation',
        text: 'Site preparation, fuel systems, switchgear, wiring and commissioning are handled end to end by our own teams.',
      },
      {
        icon: 'ri-customer-service-2-line',
        title: 'Round-the-clock support',
        text: '24/7 emergency response and planned preventive maintenance keep your power systems ready at all times.',
      },
      {
        icon: 'ri-shield-check-line',
        title: 'Genuine parts & brands',
        text: 'We supply authentic components and equipment through authorised brand partnerships, backed by real warranties.',
      },
    ],
    capabilitiesHeading: 'What We Deliver',
    capabilitiesIntro:
      'A complete power service — from the first site survey to years of dependable after-sales support.',
    capabilities: [
      {
        icon: 'ri-flashlight-line',
        title: 'Generator Sales & Sizing',
        text: 'Standby and prime-power generators selected to match your load, site and operating conditions.',
      },
      {
        icon: 'ri-tools-line',
        title: 'Installation & Commissioning',
        text: 'Professional installation, testing and handover with full documentation for every system.',
      },
      {
        icon: 'ri-settings-3-line',
        title: 'Preventive Maintenance',
        text: 'Scheduled servicing and inspections that prevent breakdowns and extend equipment life.',
      },
      {
        icon: 'ri-service-line',
        title: 'Emergency Repair — 24/7',
        text: 'Rapid response breakdown support so an unexpected fault never becomes a long outage.',
      },
      {
        icon: 'ri-archive-2-line',
        title: 'Genuine Spare Parts',
        text: 'Authentic spares, consumables and accessories supplied through authorised channels.',
      },
      {
        icon: 'ri-oil-line',
        title: 'Fuel & Control Systems',
        text: 'Fuel storage, delivery and control-panel solutions integrated with your generator system.',
      },
    ],
    faqs: [
      {
        question: 'How do I know what size generator my business needs?',
        answer:
          'Our engineers carry out a load assessment of your facility, reviewing connected equipment, starting loads and duty cycle, then recommend the correct rating with headroom for growth.',
      },
      {
        question: 'Do you install the generator as well as supply it?',
        answer:
          'Yes. We provide turnkey installation including foundations, fuel systems, switchgear, wiring, testing and commissioning, so you deal with a single accountable team.',
      },
      {
        question: 'What maintenance do you provide after installation?',
        answer:
          'We offer planned preventive maintenance contracts covering servicing, inspections, consumables and genuine parts, alongside 24/7 emergency breakdown support.',
      },
      {
        question: 'Can you service generators you did not supply?',
        answer:
          'In most cases, yes. Our service teams work across major generator brands and can take over the maintenance of your existing equipment.',
      },
      {
        question: 'How quickly can you respond to a breakdown?',
        answer:
          'We operate round-the-clock emergency support with regional teams positioned to respond quickly and get you back up and running.',
      },
    ],
  },
  {
    slug: 'fire-safety',
    name: 'Fire Safety & Firefighting Solutions',
    shortName: 'Fire Safety',
    icon: 'ri-fire-line',
    eyebrow: 'Fire Safety & Firefighting',
    heroTitle: 'Protection Designed Around Your Risk',
    heroDescription:
      'Certified fire detection, suppression and firefighting systems that safeguard people, property and operations — assessed, installed and maintained to recognised standards.',
    heroImage:
      `${import.meta.env.BASE_URL}images/6da781b727a5341f.webp`,
    overviewHeading: 'Fire Safety That Starts With Understanding the Risk',
    overviewParagraphs: [
      'Effective fire protection is never one-size-fits-all. It begins with a proper assessment of your building, your operations and the people inside them — then a system designed to match that exact risk profile.',
      'John Holt delivers the full spectrum: risk assessments and compliance audits, detection and alarm systems, suppression and extinguishing solutions, firefighting equipment supply, installation, commissioning, maintenance and staff training.',
    ],
    overviewImage:
      `${import.meta.env.BASE_URL}images/5b0664f5205430b5.jpg`,
    strengthsHeading: 'Why Clients Trust Our Fire Team',
    strengthsIntro:
      'Compliance, reliability and clear documentation — so you are always inspection-ready and genuinely protected.',
    strengths: [
      {
        icon: 'ri-file-shield-2-line',
        title: 'Standards-led design',
        text: 'Systems are designed and installed to recognised fire safety standards, with compliance documentation provided.',
      },
      {
        icon: 'ri-search-eye-line',
        title: 'Assessment before hardware',
        text: 'We start with a genuine risk assessment, so every detector and extinguisher earns its place.',
      },
      {
        icon: 'ri-user-star-line',
        title: 'People, not just systems',
        text: 'We train your teams on equipment use and emergency response, because the human factor saves lives.',
      },
      {
        icon: 'ri-refresh-line',
        title: 'Maintained, not forgotten',
        text: 'Planned servicing and inspections keep detection and suppression systems ready every single day.',
      },
    ],
    capabilitiesHeading: 'What We Deliver',
    capabilitiesIntro:
      'A complete fire safety service — from compliance audit through to training and ongoing maintenance.',
    capabilities: [
      {
        icon: 'ri-clipboard-line',
        title: 'Risk Assessments & Audits',
        text: 'Detailed fire risk assessments and compliance audits that identify hazards and prioritise action.',
      },
      {
        icon: 'ri-alarm-warning-line',
        title: 'Detection & Alarm Systems',
        text: 'Smoke, heat and multi-sensor detection with alarms and warning systems tailored to your site.',
      },
      {
        icon: 'ri-water-flash-line',
        title: 'Suppression & Extinguishing',
        text: 'Sprinkler, gas, foam and specialised suppression systems engineered for the hazard they protect.',
      },
      {
        icon: 'ri-fire-line',
        title: 'Firefighting Equipment Supply',
        text: 'Extinguishers, hydrants, hose reels and firefighting equipment supplied and commissioned.',
      },
      {
        icon: 'ri-tools-line',
        title: 'Installation & Commissioning',
        text: 'Certified installation, testing and commissioning with clear as-built documentation.',
      },
      {
        icon: 'ri-graduation-cap-line',
        title: 'Maintenance & Training',
        text: 'Scheduled servicing, inspections and staff fire-response training to keep everyone prepared.',
      },
    ],
    faqs: [
      {
        question: 'Are your fire safety systems certified?',
        answer:
          'Our detection and suppression solutions are designed and installed to recognised fire safety standards, and we provide compliance documentation for each project.',
      },
      {
        question: 'Can you carry out a fire risk assessment for us?',
        answer:
          'Yes. We conduct detailed fire risk assessments that identify hazards, evaluate existing controls and recommend clear, prioritised actions.',
      },
      {
        question: 'Do you supply firefighting equipment as well as install it?',
        answer:
          'We supply, install and commission extinguishers, hydrants, hose reels and other firefighting equipment, then support it with maintenance.',
      },
      {
        question: 'Do you provide training for our staff?',
        answer:
          'Yes. We deliver practical fire-response and equipment-use training so your teams know exactly what to do in an emergency.',
      },
      {
        question: 'How often should fire systems be serviced?',
        answer:
          'Servicing frequency depends on the system and applicable standards. We set up a maintenance schedule that keeps you compliant and always inspection-ready.',
      },
    ],
  },
  {
    slug: 'engineering-services',
    name: 'Engineering & Technical Services',
    shortName: 'Engineering Services',
    icon: 'ri-tools-line',
    eyebrow: 'Engineering & Technical Services',
    heroTitle: 'Expertise That Keeps Operations Moving',
    heroDescription:
      'End-to-end electrical, mechanical and industrial engineering — installation, retrofits, planned maintenance and technical consultancy backed by decades of know-how.',
    heroImage:
      `${import.meta.env.BASE_URL}images/5dd5394b6929a2d4.jpg`,
    overviewHeading: 'Engineering Support You Can Build a Business On',
    overviewParagraphs: [
      'Behind every reliable operation is engineering done properly. John Holt provides the technical capability that keeps industries, institutions and infrastructure running — from electrical and mechanical works to industrial installation, retrofits and planned maintenance.',
      'Our teams bring decades of hands-on experience and a practical, safety-first approach, supported by design and documentation that gives you confidence long after the work is done.',
    ],
    overviewImage:
      `${import.meta.env.BASE_URL}images/684eb47678b0e5b7.jpg`,
    strengthsHeading: 'Why Clients Partner With Our Engineers',
    strengthsIntro:
      'Multidisciplinary expertise, disciplined execution and support that continues well beyond handover.',
    strengths: [
      {
        icon: 'ri-stack-line',
        title: 'Multidisciplinary teams',
        text: 'Electrical, mechanical and installation specialists working together under one accountable partner.',
      },
      {
        icon: 'ri-shield-star-line',
        title: 'Safety-first execution',
        text: 'Every job is planned and delivered with strict safety discipline and minimal disruption to your operations.',
      },
      {
        icon: 'ri-line-chart-line',
        title: 'Planned, not reactive',
        text: 'Structured maintenance programmes that reduce downtime and extend the life of your assets.',
      },
      {
        icon: 'ri-draft-line',
        title: 'Clear documentation',
        text: 'Design drawings, method statements and as-built records handed over so nothing is left to guesswork.',
      },
    ],
    capabilitiesHeading: 'What We Deliver',
    capabilitiesIntro:
      'A broad technical skill set covering design, installation, maintenance and consultancy.',
    capabilities: [
      {
        icon: 'ri-plug-2-line',
        title: 'Electrical Engineering',
        text: 'Power distribution, control systems and electrical works designed and executed to standard.',
      },
      {
        icon: 'ri-settings-5-line',
        title: 'Mechanical Engineering',
        text: 'Mechanical systems, machinery works and plant support delivered by experienced engineers.',
      },
      {
        icon: 'ri-building-3-line',
        title: 'Industrial Installation & Retrofits',
        text: 'New installations and upgrades of existing plant and equipment, executed with minimal downtime.',
      },
      {
        icon: 'ri-calendar-check-line',
        title: 'Planned Maintenance',
        text: 'Structured maintenance programmes that keep critical systems running reliably year after year.',
      },
      {
        icon: 'ri-lightbulb-flash-line',
        title: 'Technical Consultancy',
        text: 'Engineering advice, feasibility input and design support for projects of every scale.',
      },
      {
        icon: 'ri-file-list-3-line',
        title: 'Design & Documentation',
        text: 'Drawings, specifications and as-built records that make your systems easy to operate and maintain.',
      },
    ],
    faqs: [
      {
        question: 'What kinds of engineering projects do you take on?',
        answer:
          'We handle electrical, mechanical and industrial installation work — from focused upgrades and retrofits to complete engineering packages for facilities and infrastructure.',
      },
      {
        question: 'Do you work on existing systems or only new installations?',
        answer:
          'Both. We install new systems and also extend, repair, upgrade and maintain existing plant and equipment across many industries.',
      },
      {
        question: 'Can you provide ongoing maintenance after a project?',
        answer:
          'Yes. We offer planned maintenance programmes that keep your systems performing after the project is handed over.',
      },
      {
        question: 'Do you provide design and documentation?',
        answer:
          'We provide engineering design, drawings, specifications and as-built documentation so your systems are fully understood and maintainable.',
      },
      {
        question: 'Which industries do you support?',
        answer:
          'We support manufacturing, oil and gas, banking, healthcare, government, retail, hospitality, education and more.',
      },
    ],
  },
  {
    slug: 'trading-distribution',
    name: 'Trading & Distribution',
    shortName: 'Trading & Distribution',
    icon: 'ri-ship-2-line',
    eyebrow: 'Trading & Distribution',
    heroTitle: 'Genuine Equipment, Delivered Nationwide',
    heroDescription:
      'A trusted distribution network bringing quality equipment and verified brands to businesses across Nigeria — backed by dependable supply chains and genuine parts.',
    heroImage:
      `${import.meta.env.BASE_URL}images/18fae3f7c0033a93.jpg`,
    overviewHeading: 'Supply Chains You Can Depend On',
    overviewParagraphs: [
      'Getting the right equipment at the right time is what keeps operations running. John Holt combines a nationwide distribution network with authorised brand partnerships to supply genuine equipment, spares and materials where they are needed.',
      'From sourcing and procurement through to logistics and delivery, we manage the supply chain end to end so your projects are never held up by missing parts or uncertain quality.',
    ],
    overviewImage:
      `${import.meta.env.BASE_URL}images/18fae3f7c0033a93.jpg`,
    strengthsHeading: 'Why Clients Buy Through Us',
    strengthsIntro:
      'Authentic products, dependable availability and a network that reaches across the country.',
    strengths: [
      {
        icon: 'ri-verified-badge-line',
        title: 'Genuine products only',
        text: 'We supply authentic equipment and spares through authorised brand partnerships, with real warranties behind them.',
      },
      {
        icon: 'ri-map-pin-2-line',
        title: 'Nationwide reach',
        text: 'A distribution network that delivers to businesses and sites across multiple states.',
      },
      {
        icon: 'ri-truck-line',
        title: 'Dependable logistics',
        text: 'Sourcing, procurement and delivery managed end to end so your projects stay on schedule.',
      },
      {
        icon: 'ri-stack-line',
        title: 'Deep product range',
        text: 'From generators and fire equipment to industrial spares and materials, all from one trusted source.',
      },
    ],
    capabilitiesHeading: 'What We Deliver',
    capabilitiesIntro: 'A complete supply and distribution service for businesses across Nigeria.',
    capabilities: [
      {
        icon: 'ri-shopping-bag-3-line',
        title: 'Equipment & Spare Parts Supply',
        text: 'Genuine equipment, spares and consumables supplied to keep your operations running.',
      },
      {
        icon: 'ri-road-map-line',
        title: 'Nationwide Distribution',
        text: 'A distribution network that reaches businesses and project sites across the country.',
      },
      {
        icon: 'ri-award-line',
        title: 'Authorised Brand Partnerships',
        text: 'Verified brands and authentic products backed by formal partnerships and warranties.',
      },
      {
        icon: 'ri-route-line',
        title: 'Sourcing & Procurement',
        text: 'Reliable sourcing of hard-to-find items through established supplier relationships.',
      },
      {
        icon: 'ri-truck-line',
        title: 'Logistics & Delivery',
        text: 'Coordinated logistics that get the right goods to the right place, on time.',
      },
      {
        icon: 'ri-hand-coin-line',
        title: 'Supply Chain Support',
        text: 'Practical support that keeps your procurement predictable and cost-effective.',
      },
    ],
    faqs: [
      {
        question: 'Are the products you supply genuine?',
        answer:
          'Yes. We supply authentic equipment and spares through authorised brand partnerships, so you get genuine products with real warranties.',
      },
      {
        question: 'Do you deliver outside Lagos?',
        answer:
          'Our distribution network reaches businesses and project sites across multiple states nationwide.',
      },
      {
        question: 'Can you source specific or hard-to-find items?',
        answer:
          'We use established supplier relationships to source specific and hard-to-find equipment, spares and materials on request.',
      },
      {
        question: 'Do you supply parts for equipment you did not sell?',
        answer:
          'In many cases, yes. We source genuine spares across a range of equipment and can help you identify the right parts.',
      },
    ],
  },
];

export const getServiceDetail = (slug: string): ServiceDetailData | undefined =>
  serviceDetails.find((d) => d.slug === slug);